/**
 * Thrown splash / lingering potions (vanilla ThrownPotion): launched at 0.5 blocks/tick with a
 * -20 degree pitch offset, gravity 0.05, drag 0.99. On impact a splash potion affects everything
 * within 4 blocks horizontally / 2 vertically, scaled by 1 - distance/4 (1 for a direct hit); a
 * lingering potion leaves a 3-block cloud for 30 s that re-applies its (quarter-length) effects.
 * DOM-free; render/potions.ts draws them.
 *
 *   sim.throwPotion(x, y, z, yaw, pitch, stack)   spawn
 *   sim.tick(ctx)                                 fly / break / tick clouds
 */

import { collisionOf, IS_WATERY } from '../../blocks';
import { WORLD_HEIGHT } from '../../constants';
import type { World } from '../../world/chunk';
import type { Mob } from '../../entities/mob';
import { I } from '../itemIds';
import { potionEffects, potionOf, type PotionEffect } from './potions';

export interface ThrownPotion {
  readonly id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  age: number;
  dead: boolean;
  /** Item id (SPLASH_POTION / LINGERING_POTION) and potion index. */
  item: number;
  dmg: number;
}

export interface PotionCloud {
  x: number; y: number; z: number;
  age: number;
  readonly life: number;
  readonly baseRadius: number;
  readonly color: number;
  readonly effects: readonly PotionEffect[];
  /** Entity key -> tick it may be hit again. */
  readonly cooldown: Map<string, number>;
}

export interface SplashPlayer { key: string; x: number; y: number; z: number; apply(effects: readonly PotionEffect[], scale: number): void }

export interface SplashCtx {
  world: World;
  raycastMob(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { mob: Mob; dist: number } | null;
  /** Living mobs whose box centre is within `r` of the point (cheap broad phase is fine). */
  mobsNear(x: number, y: number, z: number, r: number): Mob[];
  /** Local player's feet position and 'is alive and not spectator'. */
  player(): { x: number; y: number; z: number; ok: boolean };
  applyToMob(m: Mob, effects: readonly PotionEffect[], scale: number): void;
  applyToPlayer(effects: readonly PotionEffect[], scale: number): void;
  /** Dedicated server: every living player near the point, instead of the one local `player()`. `key` is stable per player. */
  players?(): readonly SplashPlayer[];
  /** Glass-shatter burst coloured by the potion. */
  shatter(x: number, y: number, z: number, color: number, lingering: boolean): void;
  loaded?(x: number, z: number): boolean;
}

export const POTION_SPEED = 0.5;
export const POTION_GRAVITY = 0.05;
export const SPLASH_RADIUS = 4;
export const CLOUD_LIFE = 600;
export const CLOUD_RADIUS = 3;
const MAX_AGE = 1200;

/** Effect scale for a target `dist` blocks from the impact (vanilla 1 - d / 4, 1 on a direct hit). */
export function splashScale(dist: number, direct = false): number {
  if (direct) return 1;
  return dist >= SPLASH_RADIUS ? 0 : 1 - dist / SPLASH_RADIUS;
}

export class PotionSim {
  readonly potions: ThrownPotion[] = [];
  readonly clouds: PotionCloud[] = [];
  private nextId = 1;
  private tickNo = 0;

  /** Throw from the eye (player convention: forward = (-sin yaw, sin pitch, -cos yaw)); vanilla lifts the aim by 20 degrees. */
  throwPotion(x: number, y: number, z: number, yaw: number, pitch: number, item: number, dmg: number): ThrownPotion {
    const p = pitch + 20 * Math.PI / 180;
    const cp = Math.cos(p);
    const e: ThrownPotion = {
      id: this.nextId++, x, y, z, px: x, py: y, pz: z,
      vx: -Math.sin(yaw) * cp * POTION_SPEED, vy: Math.sin(p) * POTION_SPEED, vz: -Math.cos(yaw) * cp * POTION_SPEED,
      age: 0, dead: false, item, dmg,
    };
    this.potions.push(e);
    return e;
  }

  tick(ctx: SplashCtx): void {
    this.tickNo++;
    for (let i = this.potions.length - 1; i >= 0; i--) {
      const e = this.potions[i];
      e.px = e.x; e.py = e.y; e.pz = e.z;
      if (ctx.loaded && !ctx.loaded(e.x, e.z)) continue;
      this.step(e, ctx);
      if (e.dead || ++e.age > MAX_AGE || e.y < -64) this.potions.splice(i, 1);
    }
    for (let i = this.clouds.length - 1; i >= 0; i--) {
      const c = this.clouds[i];
      if (++c.age >= c.life) { this.clouds.splice(i, 1); continue; }
      if (c.age % 5 === 0) this.cloudTick(c, ctx);
    }
  }

  private step(e: ThrownPotion, ctx: SplashCtx): void {
    const len = Math.hypot(e.vx, e.vy, e.vz);
    let tBlock = Infinity;
    const steps = Math.max(1, Math.ceil(len / 0.1));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const x = e.x + e.vx * t, y = e.y + e.vy * t, z = e.z + e.vz * t;
      if (y < 0 || y >= WORLD_HEIGHT) continue;
      if (solidAt(ctx.world, x, y, z)) { tBlock = t; break; }
    }
    const hit = len > 1e-6 ? ctx.raycastMob(e.x, e.y, e.z, e.vx, e.vy, e.vz, len) : null;
    const tMob = hit ? hit.dist / len : Infinity;
    if (hit && tMob <= tBlock) {
      e.x += e.vx * tMob; e.y += e.vy * tMob; e.z += e.vz * tMob;
      this.burst(e, ctx, hit.mob);
      return;
    }
    if (tBlock <= 1) {
      const t = Math.max(0, tBlock - 0.1 / Math.max(len, 0.1));
      e.x += e.vx * t; e.y += e.vy * t; e.z += e.vz * t;
      this.burst(e, ctx, null);
      return;
    }
    e.x += e.vx; e.y += e.vy; e.z += e.vz;
    const inWater = IS_WATERY[ctx.world.getBlock(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z))] === 1;
    const drag = inWater ? 0.8 : 0.99;
    e.vx *= drag; e.vy *= drag; e.vz *= drag;
    e.vy -= POTION_GRAVITY;
  }

  /** Impact: splash the area, or leave a cloud. */
  private burst(e: ThrownPotion, ctx: SplashCtx, direct: Mob | null): void {
    e.dead = true;
    const def = potionOf(e.dmg);
    const lingering = e.item === I.LINGERING_POTION;
    ctx.shatter(e.x, e.y, e.z, def.color, lingering);
    const effects = potionEffects(e.item, e.dmg);
    if (def.base || effects.length === 0) return;
    if (lingering) {
      this.clouds.push({
        x: e.x, y: e.y, z: e.z, age: 0, life: CLOUD_LIFE, baseRadius: CLOUD_RADIUS, color: def.color, effects,
        cooldown: new Map(),
      });
      return;
    }
    this.area(ctx, e.x, e.y, e.z, effects, direct);
  }

  private area(ctx: SplashCtx, x: number, y: number, z: number, effects: readonly PotionEffect[], direct: Mob | null): void {
    for (const m of ctx.mobsNear(x, y, z, SPLASH_RADIUS + 2)) {
      if (!m.alive) continue;
      const d = reachDist(x, y, z, m.x, m.y, m.z, m.width, m.height);
      if (d === null) continue;
      const k = splashScale(d, m === direct);
      if (k > 0) ctx.applyToMob(m, effects, k);
    }
    for (const o of ctx.players?.() ?? []) {
      const d = reachDist(x, y, z, o.x, o.y, o.z, 0.6, 1.8);
      if (d === null) continue;
      const k = splashScale(d);
      if (k > 0) o.apply(effects, k);
    }
    const p = ctx.player();
    if (p.ok) {
      const d = reachDist(x, y, z, p.x, p.y, p.z, 0.6, 1.8);
      if (d !== null) {
        const k = splashScale(d);
        if (k > 0) ctx.applyToPlayer(effects, k);
      }
    }
  }

  private cloudTick(c: PotionCloud, ctx: SplashCtx): void {
    const r = c.baseRadius * (1 - c.age / c.life);
    if (r < 0.5) return;
    for (const m of ctx.mobsNear(c.x, c.y, c.z, r + 2)) {
      if (!m.alive) continue;
      const key = 'm' + m.id;
      if ((c.cooldown.get(key) ?? 0) > this.tickNo) continue;
      if (Math.hypot(m.x - c.x, m.z - c.z) > r || m.y > c.y + 1 || m.y + m.height < c.y) continue;
      c.cooldown.set(key, this.tickNo + 20);
      ctx.applyToMob(m, c.effects, 1);
    }
    for (const o of ctx.players?.() ?? []) {
      const key = 'p' + o.key;
      if ((c.cooldown.get(key) ?? 0) > this.tickNo || Math.hypot(o.x - c.x, o.z - c.z) > r || o.y > c.y + 1 || o.y + 1.8 < c.y) continue;
      c.cooldown.set(key, this.tickNo + 20);
      o.apply(c.effects, 1);
    }
    const p = ctx.player();
    if (p.ok && (c.cooldown.get('p') ?? 0) <= this.tickNo && Math.hypot(p.x - c.x, p.z - c.z) <= r && p.y <= c.y + 1 && p.y + 1.8 >= c.y) {
      c.cooldown.set('p', this.tickNo + 20);
      ctx.applyToPlayer(c.effects, 1);
    }
  }

  clear(): void { this.potions.length = 0; this.clouds.length = 0; }
}

/** Horizontal distance from the impact to the target's box when inside the vanilla 4 x 2 x 4 inflate, else null. */
export function reachDist(ix: number, iy: number, iz: number, tx: number, ty: number, tz: number, w: number, h: number): number | null {
  const dx = Math.max(Math.abs(ix - tx) - w / 2, 0);
  const dz = Math.max(Math.abs(iz - tz) - w / 2, 0);
  const dy = iy < ty ? ty - iy : iy > ty + h ? iy - (ty + h) : 0;
  if (dx > SPLASH_RADIUS || dz > SPLASH_RADIUS || dy > 2) return null;
  const d = Math.hypot(Math.abs(ix - tx), iy - (ty + h / 2), Math.abs(iz - tz));
  return d < SPLASH_RADIUS ? d : null;
}

function solidAt(w: World, x: number, y: number, z: number): boolean {
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  const sh = collisionOf(w.getBlock(bx, by, bz));
  const lx = (x - bx) * 16, ly = (y - by) * 16, lz = (z - bz) * 16;
  for (let s = 0; s < sh.length; s += 6) {
    if (lx >= sh[s] && lx <= sh[s + 3] && ly >= sh[s + 1] && ly <= sh[s + 4] && lz >= sh[s + 2] && lz <= sh[s + 5]) return true;
  }
  return false;
}
